import React, { createContext, useContext, useState, useEffect } from 'react';

/**
 * AdminPanelContext — persists ONLY the Admin Panel tab selections across
 * navigation AND F5 refresh. Lives above the router so it never unmounts when
 * switching pages.
 *
 * Scope per product decision:
 *   - Top-level Admin Panel sub-tab (users / devices / commands / features /
 *     system / isolation-audit).
 *   - Nested System Settings sub-tab (comms / billing / maps / api / fuel /
 *     sim / accounting / maintenance / retention / security) — because
 *     `GlobalSettings.jsx` unmounts when leaving the System Settings tab and
 *     its local activeTab would otherwise reset to 'comms' on return.
 *
 * Nothing else (filters, forms, dialogs, server data) is persisted.
 *
 * Pattern: Direct Consume (same as MediaContext).
 */
const AdminPanelContext = createContext(null);

export const useAdminPanel = () => {
  const ctx = useContext(AdminPanelContext);
  if (!ctx) throw new Error('useAdminPanel must be used within AdminPanelProvider');
  return ctx;
};

const STORAGE_KEY = 'ap_state';

const loadPersistedState = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return null;
};

export const AdminPanelProvider = ({ children }) => {
  const saved = loadPersistedState();

  const [persistedActiveTab, setPersistedActiveTab] = useState(saved?.activeTab || null);
  const [persistedSystemSettingsTab, setPersistedSystemSettingsTab] = useState(saved?.systemSettingsTab || null);

  // Transient (NOT persisted) cross-tab action, e.g. Beacon Management → "Register in Device
  // Management" deep-links here so DeviceManagement opens Add Device + Auto-detect on mount.
  const [pendingDeviceAction, setPendingDeviceAction] = useState(null);

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        activeTab: persistedActiveTab,
        systemSettingsTab: persistedSystemSettingsTab,
      }));
    } catch { /* ignore */ }
  }, [persistedActiveTab, persistedSystemSettingsTab]);

  return (
    <AdminPanelContext.Provider value={{
      persistedActiveTab, setPersistedActiveTab,
      persistedSystemSettingsTab, setPersistedSystemSettingsTab,
      pendingDeviceAction, setPendingDeviceAction,
    }}>
      {children}
    </AdminPanelContext.Provider>
  );
};
